import { useState, useEffect, useCallback, useRef } from "react";
import { createPortal } from "react-dom";
import {
  FaSearch, FaSync, FaFilter, FaTimes, FaEye, FaTrash, FaPlus, FaMinus,
  FaCalendarAlt, FaTags, FaChevronDown, FaUser, FaFileInvoice,
  FaSave, FaEdit, FaDownload, FaExclamationTriangle, FaCheckCircle, 
  FaFileExcel, FaRupeeSign, FaReceipt
} from "react-icons/fa";
import { useTranslation } from "react-i18next";
import * as XLSX from "xlsx";

const API = process.env.REACT_APP_API_URL || "http://localhost:5000";

/* ═══════════════════════════════════════════════════
   TOAST COMPONENT
═══════════════════════════════════════════════════ */
function Toast({ message, type, onClose }) {
  const { t } = useTranslation();
  useEffect(() => {
    const timer = setTimeout(onClose, 5000);
    return () => clearTimeout(timer);
  }, [onClose]);

  const config = {
    success: { icon: <FaCheckCircle />, label: "Success", accentColor: "#2a6b2a", iconBg: "rgba(42,107,42,0.12)", borderColor: "rgba(42,107,42,0.3)", symbol: "✦" },
    error:   { icon: <FaTimes />, label: "Error",   accentColor: "#8b1a1a", iconBg: "rgba(139,26,26,0.1)",  borderColor: "rgba(139,26,26,0.28)", symbol: "✗" },
    info:    { icon: <FaSearch />, label: "Info",    accentColor: "#8a6020", iconBg: "rgba(201,151,58,0.1)", borderColor: "rgba(201,151,58,0.3)", symbol: "ॐ" }
  };
  const c = config[type] || config.info;

  return (
    <div className="adv-toast-container">
      <div className="adv-toast-card" style={{ "--accent": c.accentColor, "--border-color": c.borderColor }}>
        <div className="adv-toast-top-bar" />
        <span className="adv-toast-corner tl" /><span className="adv-toast-corner tr" />
        <span className="adv-toast-corner bl" /><span className="adv-toast-corner br" />
        <div className="adv-toast-medallion" style={{ background: c.iconBg, borderColor: c.borderColor }}>
          <div className="adv-toast-spin-ring" />
          <div className="adv-toast-icon-inner" style={{ color: c.accentColor }}>{c.icon}</div>
        </div>
        <div className="adv-toast-content">
          <div className="adv-toast-label-row">
            <span className="adv-toast-symbol" style={{ color: c.accentColor }}>{c.symbol}</span>
            <h4 className="adv-toast-title" style={{ color: c.accentColor }}>{c.label}</h4>
            <span className="adv-toast-symbol" style={{ color: c.accentColor }}>{c.symbol}</span>
          </div>
          <div className="adv-toast-rule" style={{ background: `linear-gradient(90deg,transparent,${c.accentColor}55,transparent)` }} />
          <p className="adv-toast-message">{message}</p>
        </div>
        <button className="adv-toast-close" onClick={onClose} style={{ color: c.accentColor }}>✕</button>
        <div className="adv-toast-bottom-rule" />
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════
   CONFIRM DIALOG
═══════════════════════════════════════════════════ */
function ConfirmDialog({ message, onConfirm, onCancel, confirmLabel = "Confirm", danger = false, title = "Confirm Action", icon = null }) {
  const { t } = useTranslation();
  const iconEl = icon || (danger ? <FaExclamationTriangle /> : <FaCheckCircle />);
  const accentColor = danger ? "#8b1a1a" : "#2a6b2a";
  const accentBg = danger ? "rgba(139,26,26,0.12)" : "rgba(42,107,42,0.1)";
  const accentBorder = danger ? "rgba(139,26,26,0.3)" : "rgba(42,107,42,0.28)";

  return createPortal(
    <div className="adv-modal-overlay" onClick={onCancel}>
      <div className="adv-confirm-modal" onClick={e => e.stopPropagation()}>
        <div className="adv-cm-topstrip" style={{ background: `linear-gradient(90deg, transparent, ${accentColor}88, ${accentColor}, ${accentColor}88, transparent)` }} />
        <span className="adv-cm-corner tl" /><span className="adv-cm-corner tr" />
        <span className="adv-cm-corner bl" /><span className="adv-cm-corner br" />
        <div className="adv-cm-header">
          <div className="adv-cm-icon-wrap" style={{ background: accentBg, borderColor: accentBorder }}>
            <div className="adv-cm-icon-ring" style={{ borderColor: `${accentColor}44` }} />
            <span className="adv-cm-icon" style={{ color: accentColor }}>{iconEl}</span>
          </div>
          <div className="adv-cm-title-area">
            <div className="adv-cm-eyebrow">{t("confirmActionEyebrow")}</div>
            <h3 className="adv-cm-title">{title}</h3>
          </div>
        </div>
        <div className="adv-cm-divider">
          <span className="adv-cm-divider-gem" style={{ background: accentColor }} />
        </div>
        <div className="adv-cm-body">
          <p className="adv-cm-message">{message}</p>
        </div>
        <div className="adv-cm-actions">
          <button className="adv-btn adv-btn-ghost" onClick={onCancel}>{t("btnCancel")}</button>
          <button
            className="adv-btn adv-cm-confirm-btn"
            style={{ "--cbg": danger ? "linear-gradient(135deg,#5a0000,#8b1a1a)" : "linear-gradient(135deg,#1a4a1a,#2a6b2a)", "--cborder": accentBorder }}
            onClick={onConfirm}
          >
            {iconEl} {confirmLabel}
          </button>
        </div>
        <div className="adv-cm-bottomstrip" />
      </div>
    </div>,
    document.body
  );
}

/* ═══════════════════════════════════════════════════
   EXPENSE FORM MODAL (Add)
   Supports: "Add More" dynamic rows
═══════════════════════════════════════════════════ */
function ExpenseFormModal({ categories, onClose, onSaved, showToast }) {
  const { t } = useTranslation();

  const today = new Date().toISOString().split("T")[0];

  // Category & Date selected ONCE at top, applied to all rows
  const [globalCategoryId, setGlobalCategoryId] = useState("");
  const [globalDate, setGlobalDate] = useState(today);
  // Each row: { id, title, amount } — date & category shared globally
  const [rows, setRows] = useState([{ id: 1, title: "", amount: "" }]);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  const addRow = () => {
    setRows(prev => [...prev, { id: Date.now(), title: "", amount: "" }]);
  };

  const removeRow = (id) => {
    if (rows.length > 1) setRows(rows.filter(r => r.id !== id));
  };

  const handleRowChange = (id, field, value) => {
    setRows(rows.map(r => r.id === id ? { ...r, [field]: value } : r));
    if (errors[id]?.[field]) {
      setErrors(prev => {
        const n = { ...prev };
        delete n[id][field];
        if (Object.keys(n[id]).length === 0) delete n[id];
        return n;
      });
    }
  };

  const validate = () => {
    const newErrors = {};
    if (!globalCategoryId) newErrors.globalCategory = "Please select a category";
    if (!globalDate) newErrors.globalDate = "Date is required";
    rows.forEach(row => {
      const rowErr = {};
      if (!row.title.trim()) rowErr.title = "Title required";
      if (!row.amount || isNaN(row.amount) || parseFloat(row.amount) <= 0) rowErr.amount = "Valid amount required";
      if (Object.keys(rowErr).length > 0) newErrors[row.id] = rowErr;
    });
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setLoading(true);
    try {
      const payload = rows.map(r => ({
        category_id: parseInt(globalCategoryId),
        title: r.title.trim(),
        amount: parseFloat(r.amount),
        expense_date: globalDate
      }));
      const res = await fetch(`${API}/api/admin/expenses/bulk`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ expenses: payload })
      });
      if (!res.ok) throw new Error("Failed to add expenses");
      showToast("Expenses added successfully", "success");
      onSaved();
      onClose();
    } catch (err) {
      showToast(err.message, "error");
    } finally {
      setLoading(false);
    }
  };

  const rowTotal = rows.reduce((sum, r) => sum + (parseFloat(r.amount) || 0), 0);

  return createPortal(
    <div className="adv-modal-overlay" onClick={onClose}>
      <div className="adv-regal-modal adv-exp-form-regal" onClick={e => e.stopPropagation()}>
        <div className="adv-rm-topglow" />
        <span className="adv-cm-corner tl" /><span className="adv-cm-corner tr" />
        <span className="adv-cm-corner bl" /><span className="adv-cm-corner br" />

        {/* ── HEADER ── */}
        <div className="adv-rm-header">
          <div className="adv-rm-header-bg" />
          <div className="adv-rm-header-inner">
            <div className="adv-rm-header-icon-wrap">
              <div className="adv-rm-hicon-ring" />
              <FaReceipt className="adv-rm-hicon" />
            </div>
            <div className="adv-rm-header-eyebrow">✦ {t("expModTitle") || "Expense Management"} ✦</div>
            <h2 className="adv-rm-title">{t("expModAdd") || "Record Expenses"}</h2>
            <p className="adv-rm-name-tag">Add one or more expenses at once</p>
          </div>
          <button className="adv-rm-close" onClick={onClose}><FaTimes /></button>
        </div>
        <div className="adv-rm-rule"><span className="adv-rm-rule-ornament">❖</span></div>

        {/* ── SCROLLABLE BODY ── */}
        <div className="adv-exp-scroll-body">

          <div className="adv-edit-section-label">✦ {t("expSecDetails") || "Expense Details"}</div>

          {/* ── DATE & CATEGORY — selected ONCE, apply to all rows ── */}
          <div className="adv-exp-global-row">

            {/* Date — once */}
            <div className="adv-exp-once-block">
              <div className="adv-exp-once-label">
                <FaCalendarAlt className="adv-exp-once-icon" />
                <span>{t("expLblDate") || "Date"}</span>
                <span className="adv-req">*</span>
                <span className="adv-exp-once-badge">all entries</span>
              </div>
              <div className={`adv-exp-date-wrap ${errors.globalDate ? "err" : ""}`}>
                <FaCalendarAlt className="adv-exp-sel-icon" />
                <input
                  type="date"
                  className="adv-exp-date-input"
                  value={globalDate}
                  onChange={e => {
                    setGlobalDate(e.target.value);
                    if (errors.globalDate) setErrors(p => { const n={...p}; delete n.globalDate; return n; });
                  }}
                />
              </div>
              {errors.globalDate && <span className="adv-edit-err">{errors.globalDate}</span>}
            </div>

            {/* Category — once */}
            <div className="adv-exp-once-block">
              <div className="adv-exp-once-label">
                <FaTags className="adv-exp-once-icon" />
                <span>{t("expLblCategory") || "Category"}</span>
                <span className="adv-req">*</span>
                <span className="adv-exp-once-badge">all entries</span>
              </div>
              <div className={`adv-exp-select-wrap ${errors.globalCategory ? "err" : ""}`}>
                <FaTags className="adv-exp-sel-icon" />
                <select
                  className="adv-exp-select"
                  value={globalCategoryId}
                  onChange={e => {
                    setGlobalCategoryId(e.target.value);
                    if (errors.globalCategory) setErrors(p => { const n={...p}; delete n.globalCategory; return n; });
                  }}
                >
                  <option value="">— Select Category —</option>
                  {categories.map(c => (
                    <option key={c.category_id} value={c.category_id}>{c.title}</option>
                  ))}
                </select>
                <FaChevronDown className="adv-exp-sel-arrow" />
              </div>
              {errors.globalCategory && <span className="adv-edit-err">{errors.globalCategory}</span>}
            </div>

          </div>

          {/* ── COLUMN HEADERS ── */}
          <div className="adv-exp-col-headers">
            <div className="adv-exp-ch-num">#</div>
            <div className="adv-exp-ch-title"><FaFileInvoice /> Title</div>
            <div className="adv-exp-ch-amount"><FaRupeeSign /> Amount</div>
            <div className="adv-exp-ch-del" />
          </div>

          {/* ── INLINE ROWS: Title + Amount only ── */}
          <div className="adv-exp-rows-list">
            {rows.map((row, idx) => (
              <div key={row.id} className={`adv-exp-inline-row ${errors[row.id] ? "has-err" : ""}`}>

                <div className="adv-exp-ir-num">{idx + 1}</div>

                {/* Title */}
                <div className="adv-exp-ir-cell adv-exp-ir-title">
                  <input
                    type="text"
                    className={`adv-exp-ir-input${errors[row.id]?.title ? " err" : ""}`}
                    placeholder="e.g. Electricity Bill"
                    value={row.title}
                    onChange={e => handleRowChange(row.id, "title", e.target.value)}
                  />
                  {errors[row.id]?.title && <span className="adv-exp-ir-err">{errors[row.id].title}</span>}
                </div>

                {/* Amount */}
                <div className="adv-exp-ir-cell adv-exp-ir-amount">
                  <div className="adv-exp-ir-amt-wrap">
                    <span className="adv-exp-ir-rupee">₹</span>
                    <input
                      type="number"
                      className={`adv-exp-ir-input adv-exp-ir-amt-input${errors[row.id]?.amount ? " err" : ""}`}
                      placeholder="0.00"
                      value={row.amount}
                      onChange={e => handleRowChange(row.id, "amount", e.target.value)}
                      min="0"
                      step="0.01"
                    />
                  </div>
                  {errors[row.id]?.amount && <span className="adv-exp-ir-err">{errors[row.id].amount}</span>}
                </div>

                {/* Remove */}
                <div className="adv-exp-ir-del">
                  {rows.length > 1 ? (
                    <button className="adv-exp-ir-del-btn" onClick={() => removeRow(row.id)} title="Remove">
                      <FaMinus />
                    </button>
                  ) : <div className="adv-exp-ir-del-ph" />}
                </div>

              </div>
            ))}
          </div>

          {/* ── ADD ROW centered + TOTAL ── */}
          <div className="adv-exp-footer-row">
            {rows.length > 1 && rowTotal > 0 && (
              <div className="adv-exp-total-chip">
                <span className="adv-exp-total-label">Total</span>
                <span className="adv-exp-total-val">₹{rowTotal.toLocaleString("en-IN", { minimumFractionDigits: 2 })}</span>
              </div>
            )}
            <button className="adv-exp-add-more-btn" onClick={addRow}>
              <FaPlus /> Add Row
            </button>
            {rows.length > 1 && rowTotal > 0 && <div style={{width: "1px"}} />}
          </div>

          {/* ── ACTIONS ── */}
          <div className="adv-edit-actions" style={{ marginTop: "20px" }}>
            <button className="adv-btn adv-btn-ghost" onClick={onClose} disabled={loading}>{t("btnCancel")}</button>
            <button className="adv-edit-save-btn" onClick={handleSubmit} disabled={loading}>
              {loading
                ? <><span className="adv-edit-saving-spinner" /> Saving...</>
                : <><FaSave /> Save {rows.length > 1 ? `${rows.length} Expenses` : "Expense"}</>
              }
            </button>
          </div>

        </div>
      </div>
    </div>,
    document.body
  );
}


/* ═══════════════════════════════════════════════════
   EDIT EXPENSE MODAL (Single expense edit)
═══════════════════════════════════════════════════ */
function EditExpenseModal({ expense, categories, onClose, onSaved, showToast }) {
  const { t } = useTranslation();

  const [categoryId, setCategoryId] = useState(String(expense.category_id));
  const [title, setTitle] = useState(expense.title);
  const [amount, setAmount] = useState(String(expense.amount));
  const [expenseDate, setExpenseDate] = useState(() => {
    if (!expense.expense_date) return "";
    const d = new Date(expense.expense_date);
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  const validate = () => {
    const errs = {};
    if (!categoryId) errs.categoryId = "Category is required";
    if (!title.trim()) errs.title = "Title is required";
    if (!amount || isNaN(amount) || parseFloat(amount) <= 0) errs.amount = "Valid amount required";
    if (!expenseDate) errs.expenseDate = "Date is required";
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/admin/expenses/${expense.expense_id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          category_id: parseInt(categoryId),
          title: title.trim(),
          amount: parseFloat(amount),
          expense_date: expenseDate,
        }),
      });
      if (!res.ok) throw new Error("Failed to update expense");
      showToast("Expense updated successfully", "success");
      onSaved();
      onClose();
    } catch (err) {
      showToast(err.message, "error");
    } finally {
      setLoading(false);
    }
  };

  return createPortal(
    <div className="adv-modal-overlay" onClick={onClose}>
      <div className="adv-regal-modal adv-exp-form-regal" onClick={e => e.stopPropagation()}>
        <div className="adv-rm-topglow" />
        <span className="adv-cm-corner tl" /><span className="adv-cm-corner tr" />
        <span className="adv-cm-corner bl" /><span className="adv-cm-corner br" />

        {/* ── HEADER ── */}
        <div className="adv-rm-header">
          <div className="adv-rm-header-bg" />
          <div className="adv-rm-header-inner">
            <div className="adv-rm-header-icon-wrap">
              <div className="adv-rm-hicon-ring" />
              <FaEdit className="adv-rm-hicon" />
            </div>
            <div className="adv-rm-header-eyebrow">✦ {t("expModTitle") || "Expense Management"} ✦</div>
            <h2 className="adv-rm-title">Edit Expense</h2>
            <p className="adv-rm-name-tag">Update the expense details below</p>
          </div>
          <button className="adv-rm-close" onClick={onClose}><FaTimes /></button>
        </div>
        <div className="adv-rm-rule"><span className="adv-rm-rule-ornament">❖</span></div>

        {/* ── BODY ── */}
        <div className="adv-exp-scroll-body">
          <div className="adv-edit-section-label">✦ Expense Details</div>

          {/* Date & Category */}
          <div className="adv-exp-global-row">
            <div className="adv-exp-once-block">
              <div className="adv-exp-once-label">
                <FaCalendarAlt className="adv-exp-once-icon" />
                <span>{t("expLblDate") || "Date"}</span>
                <span className="adv-req">*</span>
              </div>
              <div className={`adv-exp-date-wrap ${errors.expenseDate ? "err" : ""}`}>
                <FaCalendarAlt className="adv-exp-sel-icon" />
                <input
                  type="date"
                  className="adv-exp-date-input"
                  value={expenseDate}
                  onChange={e => { setExpenseDate(e.target.value); setErrors(p => { const n={...p}; delete n.expenseDate; return n; }); }}
                />
              </div>
              {errors.expenseDate && <span className="adv-edit-err">{errors.expenseDate}</span>}
            </div>

            <div className="adv-exp-once-block">
              <div className="adv-exp-once-label">
                <FaTags className="adv-exp-once-icon" />
                <span>{t("expLblCategory") || "Category"}</span>
                <span className="adv-req">*</span>
              </div>
              <div className={`adv-exp-select-wrap ${errors.categoryId ? "err" : ""}`}>
                <FaTags className="adv-exp-sel-icon" />
                <select
                  className="adv-exp-select"
                  value={categoryId}
                  onChange={e => { setCategoryId(e.target.value); setErrors(p => { const n={...p}; delete n.categoryId; return n; }); }}
                >
                  <option value="">— Select Category —</option>
                  {categories.map(c => (
                    <option key={c.category_id} value={c.category_id}>{c.title}</option>
                  ))}
                </select>
                <FaChevronDown className="adv-exp-sel-arrow" />
              </div>
              {errors.categoryId && <span className="adv-edit-err">{errors.categoryId}</span>}
            </div>
          </div>

          {/* Title */}
          <div className="adv-exp-once-block" style={{ marginTop: "14px" }}>
            <div className="adv-exp-once-label">
              <FaFileInvoice className="adv-exp-once-icon" />
              <span>Title</span>
              <span className="adv-req">*</span>
            </div>
            <input
              type="text"
              className={`adv-exp-ir-input${errors.title ? " err" : ""}`}
              style={{ width: "100%", boxSizing: "border-box" }}
              placeholder="e.g. Electricity Bill"
              value={title}
              onChange={e => { setTitle(e.target.value); setErrors(p => { const n={...p}; delete n.title; return n; }); }}
            />
            {errors.title && <span className="adv-edit-err">{errors.title}</span>}
          </div>

          {/* Amount */}
          <div className="adv-exp-once-block" style={{ marginTop: "14px" }}>
            <div className="adv-exp-once-label">
              <FaRupeeSign className="adv-exp-once-icon" />
              <span>Amount</span>
              <span className="adv-req">*</span>
            </div>
            <div className={`adv-exp-ir-amt-wrap${errors.amount ? " err" : ""}`} style={{ display: "flex", alignItems: "center" }}>
              <span className="adv-exp-ir-rupee">₹</span>
              <input
                type="number"
                className={`adv-exp-ir-input adv-exp-ir-amt-input${errors.amount ? " err" : ""}`}
                style={{ flex: 1 }}
                placeholder="0.00"
                value={amount}
                onChange={e => { setAmount(e.target.value); setErrors(p => { const n={...p}; delete n.amount; return n; }); }}
                min="0"
                step="0.01"
              />
            </div>
            {errors.amount && <span className="adv-edit-err">{errors.amount}</span>}
          </div>

          {/* Actions */}
          <div className="adv-edit-actions" style={{ marginTop: "24px" }}>
            <button className="adv-btn adv-btn-ghost" onClick={onClose} disabled={loading}>{t("btnCancel")}</button>
            <button className="adv-edit-save-btn" onClick={handleSubmit} disabled={loading}>
              {loading
                ? <><span className="adv-edit-saving-spinner" /> Saving...</>
                : <><FaSave /> Update Expense</>
              }
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}

/* ═══════════════════════════════════════════════════
   EXPENSE DETAIL MODAL
═══════════════════════════════════════════════════ */
function ExpenseDetailModal({ expense, onClose, onDelete, onEdit }) {
  const { t } = useTranslation();
  if (!expense) return null;

  return createPortal(
    <div className="adv-modal-overlay" onClick={onClose}>
      <div className="adv-regal-modal adv-detail-regal" onClick={e => e.stopPropagation()}>
        <div className="adv-rm-topglow" />
        <span className="adv-cm-corner tl" /><span className="adv-cm-corner tr" />
        <span className="adv-cm-corner bl" /><span className="adv-cm-corner br" />
        <div className="adv-rm-header">
          <div className="adv-rm-header-bg" />
          <div className="adv-rm-header-inner">
            <div className="adv-rm-header-icon-wrap">
              <div className="adv-rm-hicon-ring" />
              <FaReceipt className="adv-rm-hicon" />
            </div>
            <div className="adv-rm-header-eyebrow">{t("expModTitle") || "Expense Management"}</div>
            <h2 className="adv-rm-title">{t("expModView") || "Expense Details"}</h2>
            <p className="adv-rm-name-tag">{new Date(expense.expense_date).toLocaleDateString("en-IN", {day:"numeric", month:"short", year:"numeric"})}</p>
          </div>
          <button className="adv-rm-close" onClick={onClose}><FaTimes /></button>
        </div>
        <div className="adv-rm-rule"><span className="adv-rm-rule-ornament">❖</span></div>
        <div className="adv-detail-scroll-body">
          <div className="adv-detail-section-label">{t("expSecSummary") || "Expense Summary"}</div>
          <div className="adv-detail-info-grid">
            <div className="adv-di-field">
              <div className="adv-di-icon"><FaTags /></div>
              <div>
                <div className="adv-di-label">{t("expColCategory") || "Category"}</div>
                <div className="adv-di-val">{expense.category_title || "Unknown"}</div>
              </div>
            </div>
            <div className="adv-di-field">
              <div className="adv-di-icon"><FaReceipt /></div>
              <div>
                <div className="adv-di-label">{t("expColTitle") || "Title"}</div>
                <div className="adv-di-val">{expense.title}</div>
              </div>
            </div>
            <div className="adv-di-field">
              <div className="adv-di-icon"><FaRupeeSign /></div>
              <div>
                <div className="adv-di-label">{t("expColAmount") || "Amount"}</div>
                <div className="adv-di-val" style={{fontWeight: "700", color: "#8b1a1a"}}>₹{parseFloat(expense.amount).toLocaleString('en-IN')}</div>
              </div>
            </div>
            <div className="adv-di-field">
              <div className="adv-di-icon"><FaCalendarAlt /></div>
              <div>
                <div className="adv-di-label">{t("expColDate") || "Date"}</div>
                <div className="adv-di-val">{new Date(expense.expense_date).toLocaleDateString("en-IN")}</div>
              </div>
            </div>
          </div>

          <div className="adv-detail-section-label" style={{marginTop: "22px"}}>{t("detailAdminActions")}</div>
          <div className="adv-detail-action-grid">
            <button className="adv-action-tile" onClick={() => { onClose(); onEdit(expense); }}>
              <FaEdit className="adv-at-icon" /><span className="adv-at-label">Edit Expense</span><span className="adv-at-desc">Update details</span>
            </button>
            <button className="adv-action-tile danger" onClick={() => onDelete(expense)}>
              <FaTrash className="adv-at-icon" /><span className="adv-at-label">{t("detailDeleteExpense") || "Delete Expense"}</span><span className="adv-at-desc">{t("detailDeleteDesc") || "Permanently remove"}</span>
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}

/* ═══════════════════════════════════════════════════
   MAIN COMPONENT: Aexpenses.js
═══════════════════════════════════════════════════ */
export default function AExpenses() {
  const { t } = useTranslation();
  const [expenses, setExpenses] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  
  // Filter States
  const [specificDate, setSpecificDate] = useState("");
  const [dateRange, setDateRange] = useState({ start: "", end: "" });
  const [selectedMonth, setSelectedMonth] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [formModal, setFormModal] = useState(null);
  const [editExpense, setEditExpense] = useState(null);
  const [logoFace, setLogoFace] = useState("om");

  useEffect(() => {
    const timer = setInterval(() => setLogoFace(f => f === "om" ? "logo" : "om"), 3000);
    return () => clearInterval(timer);
  }, []);

  // UI States
  const [toast, setToast] = useState(null);
  const [selectedExpense, setSelectedExpense] = useState(null);
  const [confirmDialog, setConfirmDialog] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [showFilterPanel, setShowFilterPanel] = useState(false);
  const searchRef = useRef(null);

  const showToast = (message, type = "info") => setToast({ message, type });

  // --- FETCH DATA ---
  const fetchCategories = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/admin/expense-categories`);
      const data = await res.json();
      setCategories(data || []);
    } catch (err) { console.error("Failed to fetch categories"); }
  }, [API]);

  const fetchExpenses = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/admin/expenses`);
      if (!res.ok) throw new Error("Failed to fetch");
      const data = await res.json();
      setExpenses(data);
      setFiltered(data);
    } catch (err) {
      showToast(t("toastFetchFailed") || "Failed to load expenses", "error");
    } finally { setLoading(false); }
  }, [API]);

  useEffect(() => { fetchCategories(); fetchExpenses(); }, [fetchCategories, fetchExpenses]);

  // --- FILTERING LOGIC (Reused from Bookings) ---
  useEffect(() => {
    let result = [...expenses];

    // 1. Search
    if (search.trim()) {
      const q = search.toLowerCase();
      result = result.filter(e => e.title?.toLowerCase().includes(q) || e.category_title?.toLowerCase().includes(q));
    }

    // 2. Category Filter
    if (selectedCategory !== "all") {
      result = result.filter(e => String(e.category_title).toLowerCase() === String(selectedCategory).toLowerCase());
    }

    // Helper for Date Handling
    const toDateStr = (val) => {
      if (!val) return "";
      const s = String(val).trim();
      if (!s.endsWith("Z")) return s.slice(0, 10);
      const d = new Date(s);
      if (isNaN(d)) return s.slice(0, 10);
      const yyyy = d.getFullYear();
      const mm   = String(d.getMonth() + 1).padStart(2, "0");
      const dd   = String(d.getDate()).padStart(2, "0");
      return `${yyyy}-${mm}-${dd}`;
    };

    // 3. Date Filters
    if (specificDate) {
      result = result.filter(e => toDateStr(e.expense_date) === specificDate);
    }

    if (!specificDate && (dateRange.start || dateRange.end)) {
      result = result.filter(e => {
        const eDate = toDateStr(e.expense_date);
        if (dateRange.start && dateRange.end) return eDate >= dateRange.start && eDate <= dateRange.end;
        if (dateRange.start) return eDate >= dateRange.start;
        if (dateRange.end) return eDate <= dateRange.end;
        return true;
      });
    }

    if (!specificDate && !dateRange.start && !dateRange.end && selectedMonth) {
      result = result.filter(e => toDateStr(e.expense_date).startsWith(selectedMonth));
    }

    setFiltered(result);
  }, [search, selectedCategory, specificDate, dateRange, selectedMonth, expenses]);

  // --- HANDLERS ---
  const handleSpecificDateChange = (val) => {
    setSpecificDate(val);
    setSelectedMonth("");
    if (val) setDateRange({ start: "", end: "" });
  };

  const handleRangeChange = (field, val) => {
    setDateRange(prev => ({ ...prev, [field]: val }));
    setSpecificDate("");
    setSelectedMonth("");
  };

  const handleDelete = (exp) => {
    setConfirmDialog({
      title: t("expConfDeleteTitle") || "Delete Expense",
      message: `Are you sure you want to delete "${exp.title}"? This action cannot be undone.`,
      confirmLabel: t("expConfDeleteBtn") || "Delete Forever",
      danger: true,
      icon: <FaTrash />,
      onConfirm: async () => {
        setActionLoading(true);
        try {
          const res = await fetch(`${API}/api/admin/expenses/${exp.expense_id}`, { method: "DELETE" });
          if (!res.ok) throw new Error("Failed to delete");
          showToast(t("expToastDeleted") || "Expense deleted successfully", "success");
          fetchExpenses();
          setSelectedExpense(null);
        } catch (err) {
          showToast(err.message, "error");
        } finally {
          setActionLoading(false);
          setConfirmDialog(null);
        }
      }
    });
  };

  // --- EXCEL EXPORT ---
  const handleExport = () => {
    if (filtered.length === 0) {
      showToast(t("toastExportEmpty") || "No data to export", "info");
      return;
    }

    const exportData = filtered.map(e => ({
      [t("expColDate") || "Date"]: new Date(e.expense_date).toLocaleDateString("en-IN"),
      [t("expColCategory") || "Category"]: e.category_title || "",
      [t("expColTitle") || "Title"]: e.title || "",
      [t("expColAmount") || "Amount"]: parseFloat(e.amount),
    }));

    const ws = XLSX.utils.json_to_sheet(exportData);
    const colKeys = Object.keys(exportData[0] || {});
    ws["!cols"] = colKeys.map(key => ({
      wch: Math.max(key.length + 2, ...exportData.map(r => String(r[key] ?? "").length + 2), 12)
    }));

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Expenses");
    const filename = `Temple_Expenses_${new Date().toISOString().slice(0, 10)}.xlsx`;
    XLSX.writeFile(wb, filename);
    showToast(t("toastExportSuccess").replace("{count}", exportData.length), "success");
  };

  const clearFilters = () => {
    setSearch("");
    setSpecificDate("");
    setDateRange({ start: "", end: "" });
    setSelectedMonth("");
    setSelectedCategory("all");
  };

  const activeFilterCount =
    (search ? 1 : 0) +
    (selectedCategory !== "all" ? 1 : 0) +
    (specificDate ? 1 : 0) +
    (dateRange.start || dateRange.end ? 1 : 0) +
    (selectedMonth ? 1 : 0);

  return (
    <>
      <AexpensesStyles />

      {/* HERO */}
      <div className="adv-hero">
        <div className="adv-hero-bg" />
        <div className="adv-hero-rings">
          {[220, 170, 125, 85].map((s, i) => (
            <div key={i} className="adv-hero-ring" style={{ width: s, height: s, animationDelay: `${i * 0.5}s` }} />
          ))}
        </div>
        <div className="adv-hero-medallion">
          <div className="adv-hero-med-ring" />
          <div className="adv-hero-med-ring adv-hero-med-ring-2" />
          <div className={`adv-hero-face ${logoFace === "om" ? "visible" : ""}`}>
            <span className="adv-hero-om">ॐ</span>
          </div>
          <div className={`adv-hero-face ${logoFace === "logo" ? "visible" : ""}`}>
            <img src="./logo.png" alt="Temple" className="adv-hero-logo-img" />
          </div>
          <div className="adv-hero-med-halo" />
        </div>
        <div className="adv-hero-text">
          <div className="adv-hero-eyebrow">✦ {t("pageEyebrow") || "Temple Records"} ✦</div>
          <h1 className="adv-hero-title">{t("expPageTitle") || "Expense Management"}</h1>
          <p className="adv-hero-sub">{t("expPageSub") || "Track and manage temple expenditures"}</p>
        </div>
        <div className="adv-hero-rule" />
        <div className="adv-stat-row">
          <div className="adv-stat-card" style={{ "--scolor": "#c9973a" }}>
            <div className="adv-stat-val">{filtered.length}</div>
            <div className="adv-stat-label">{t("statShowing") || "Showing"}</div>
          </div>
          <div className="adv-stat-card" style={{ "--scolor": "#8b1a1a" }}>
            <div className="adv-stat-val">₹{filtered.reduce((acc, curr) => acc + parseFloat(curr.amount || 0), 0).toLocaleString('en-IN')}</div>
            <div className="adv-stat-label">{t("expStatTotal") || "Total Expenses"}</div>
          </div>
        </div>
      </div>

      {/* TOOLBAR */}
      <div className="adv-toolbar">
        <div className="adv-search-wrap">
          <FaSearch className="adv-search-icon" />
          <input
            ref={searchRef}
            className="adv-search-input"
            placeholder={t("expSearchPlaceholder") || "Search expenses..."}
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
          {search && <button className="adv-search-clear" onClick={() => setSearch("")}><FaTimes /></button>}
        </div>

        <button className="adv-filter-trigger-btn" onClick={() => setShowFilterPanel(!showFilterPanel)}>
          <FaFilter /> {t("btnFilters") || "Filters"}
          {activeFilterCount > 0 && <span className="adv-filter-badge">{activeFilterCount}</span>}
          <FaChevronDown className={`adv-dd-chevron ${showFilterPanel ? "open" : ""}`} />
        </button>

        <div className="adv-toolbar-btns">
          <button className="adv-action-btn adv-btn-export" onClick={() => setFormModal(true)}>
            <FaPlus /> {t("expBtnAdd") || "Add Expense"}
          </button>
          <button className="adv-action-btn adv-btn-export" onClick={handleExport}>
            <FaFileExcel /> {t("btnExport") || "Export"}
          </button>
          {(activeFilterCount > 0) && (
            <button className="adv-action-btn adv-btn-clear" onClick={clearFilters}>
              <FaTimes /> {t("btnClear") || "Clear"}
            </button>
          )}
          <button className="adv-action-btn adv-btn-refresh" onClick={fetchExpenses}>
            <FaSync className={loading ? "spin" : ""} /> {t("btnRefresh") || "Refresh"}
          </button>
        </div>
      </div>

      {/* FILTER PANEL */}
      {showFilterPanel && (
        <div className="adv-filter-panel">
          <div className="adv-filter-row">
            {/* Category Filter */}
            <div className="adv-filter-item">
              <label className="adv-filter-label"><FaTags /> {t("filterCategory") || "Category"}</label>
              <select 
                className="adv-filter-select" 
                value={selectedCategory} 
                onChange={(e) => setSelectedCategory(e.target.value)}
              >
                <option value="all">{t("filterCategoryAll") || "All Categories"}</option>
                {categories.map(c => (
                  <option key={c.category_id} value={c.title}>{c.title}</option>
                ))}
              </select>
            </div>

            {/* Date Filters (Reused from Bookings) */}
            <div className="adv-filter-item">
              <label className="adv-filter-label"><FaCalendarAlt /> {t("filterSpecificDate") || "Specific Date"}</label>
              <input
                type="date"
                className="adv-filter-input"
                value={specificDate}
                onChange={(e) => handleSpecificDateChange(e.target.value)}
              />
              {specificDate && (
                <button className="adv-filter-clear-btn" onClick={() => setSpecificDate("")}>{t("filterClearDate")}</button>
              )}
            </div>

            <div className={`adv-filter-item adv-filter-range ${specificDate ? "adv-filter-disabled" : ""}`}>
              <label className="adv-filter-label"><FaCalendarAlt /> {t("filterDateRange") || "Date Range"} {specificDate && <span className="adv-filter-note">{t("filterClearDateFirst")}</span>}</label>
              <div className="adv-range-inputs">
                <input
                  type="date"
                  className="adv-filter-input"
                  value={dateRange.start}
                  onChange={(e) => handleRangeChange("start", e.target.value)}
                  disabled={!!specificDate}
                />
                <span className="adv-range-sep">→</span>
                <input
                  type="date"
                  className="adv-filter-input"
                  value={dateRange.end}
                  onChange={(e) => handleRangeChange("end", e.target.value)}
                  disabled={!!specificDate}
                />
              </div>
              {(dateRange.start || dateRange.end) && !specificDate && (
                <button className="adv-filter-clear-btn" onClick={() => setDateRange({ start: "", end: "" })}>{t("filterClearRange")}</button>
              )}
            </div>

            <div className={`adv-filter-item ${(specificDate || dateRange.start || dateRange.end) ? "adv-filter-disabled" : ""}`}>
              <label className="adv-filter-label">
                <FaCalendarAlt /> {t("filterMonth") || "Month"}
                {(specificDate || dateRange.start || dateRange.end) && <span className="adv-filter-note">{t("filterClearDateFiltersFirst")}</span>}
              </label>
              <input
                type="month"
                className="adv-filter-input"
                value={selectedMonth}
                onChange={(e) => { setSelectedMonth(e.target.value); setSpecificDate(""); setDateRange({ start: "", end: "" }); }}
                disabled={!!(specificDate || dateRange.start || dateRange.end)}
              />
              {selectedMonth && !(specificDate || dateRange.start || dateRange.end) && (
                <button className="adv-filter-clear-btn" onClick={() => setSelectedMonth("")}>{t("filterClearMonth")}</button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* RESULT INFO */}
      <div className="adv-result-info">
        {!loading && <span>{t("displaying") || "Displaying"} <strong>{filtered.length}</strong> {t("expenses") || "expenses"}</span>}
      </div>

      {/* TABLE */}
      <div className="adv-table-wrap">
        {loading ? (
          <div className="adv-loader-wrap"><div className="adv-loader-om">ॐ</div><p>{t("loading") || "Loading..."}</p></div>
        ) : filtered.length === 0 ? (
          <div className="adv-empty">
            <div className="adv-empty-om">ॐ</div>
            <h3 className="adv-empty-title">{t("expNoResultsTitle") || "No Expenses Found"}</h3>
            <p className="adv-empty-sub">{t("expNoResultsSub") || "No expenses match the filters."}</p>
            <button className="adv-btn adv-btn-ghost" onClick={clearFilters}>{t("btnClearFilters") || "Clear Filters"}</button>
          </div>
        ) : (
          <div className="adv-table-scroll-container">
            <table className="adv-table adv-desktop-only">
              <thead>
                <tr>
                  <th>#</th>
                  <th>{t("expColDate") || "Date"}</th>
                  <th>{t("expColCategory") || "Category"}</th>
                  <th>{t("expColTitle") || "Title"}</th>
                  <th>{t("expColAmount") || "Amount"}</th>
                  <th>{t("tableActions") || "Actions"}</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((e, i) => (
                  <tr key={e.expense_id} className="adv-table-row" style={{ animationDelay: `${i * 0.03}s` }}>
                    <td className="adv-td-num">{i + 1}</td>
                    <td className="adv-td-date">{new Date(e.expense_date).toLocaleDateString("en-IN")}</td>
                    <td>
                      <span className="adv-cat-name">{e.category_title}</span>
                    </td>
                    <td className="adv-devotee-name">{e.title}</td>
                    <td className="adv-td-mobile" style={{fontWeight: "700", color: "#8b1a1a"}}>
                      ₹{parseFloat(e.amount).toLocaleString('en-IN')}
                    </td>
                    <td>
                      <div className="adv-action-cell">
                        <button className="adv-icon-btn adv-btn-view" title="View Details" onClick={() => setSelectedExpense(e)}><FaEye /></button>
                        <button className="adv-icon-btn adv-btn-edit" title="Edit" onClick={() => setEditExpense(e)}><FaEdit /></button>
                        <button className="adv-icon-btn adv-btn-delete-sm" title="Delete" onClick={() => handleDelete(e)}><FaTrash /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Mobile Cards */}
            <div className="adv-mobile-only adv-card-list">
              {filtered.map((e, i) => (
                <div key={e.expense_id} className="adv-mob-card">
                  <div className="adv-mob-card-header">
                    <div className="adv-mob-receipt">{new Date(e.expense_date).toLocaleDateString("en-IN")}</div>
                    <span className="adv-status-pill" style={{color:"#8b1a1a", background:"rgba(139,26,26,0.1)", borderColor:"rgba(139,26,26,0.25)"}}>Expense</span>
                  </div>
                  <div className="adv-mob-meta">
                    <div className="adv-mob-meta-item"><FaTags className="adv-mob-meta-icon" /><span>{e.category_title || "Unknown"}</span></div>
                    <div className="adv-mob-meta-item"><FaReceipt className="adv-mob-meta-icon" /><span>{e.title}</span></div>
                    <div className="adv-mob-meta-item"><FaRupeeSign className="adv-mob-meta-icon" /><span style={{fontWeight:"700"}}>₹{parseFloat(e.amount).toLocaleString('en-IN')}</span></div>
                  </div>
                  <div className="adv-mob-actions">
                    <button className="adv-mob-btn mob-view" onClick={() => setSelectedExpense(e)}><FaEye /> {t("btnView") || "View"}</button>
                    <button className="adv-mob-btn mob-edit" onClick={() => setEditExpense(e)}><FaEdit /> Edit</button>
                    <button className="adv-mob-btn mob-delete" onClick={() => handleDelete(e)}><FaTrash /></button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* MODALS */}
      {formModal && (
        <ExpenseFormModal
          categories={categories}
          onClose={() => setFormModal(null)}
          onSaved={() => { setFormModal(null); fetchExpenses(); }}
          showToast={showToast}
        />
      )}
      {selectedExpense && (
        <ExpenseDetailModal
          expense={selectedExpense}
          onClose={() => setSelectedExpense(null)}
          onDelete={handleDelete}
          onEdit={(exp) => setEditExpense(exp)}
        />
      )}
      {editExpense && (
        <EditExpenseModal
          expense={editExpense}
          categories={categories}
          onClose={() => setEditExpense(null)}
          onSaved={() => { setEditExpense(null); fetchExpenses(); }}
          showToast={showToast}
        />
      )}
      {confirmDialog && (
        <ConfirmDialog
          title={confirmDialog.title}
          message={confirmDialog.message}
          confirmLabel={confirmDialog.confirmLabel}
          danger={confirmDialog.danger}
          icon={confirmDialog.icon}
          onConfirm={confirmDialog.onConfirm}
          onCancel={() => setConfirmDialog(null)}
        />
      )}
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
    </>
  );
}

/* ═══════════════════════════════════════════════════
   STYLES (Reused from Abookings + Specifics)
═══════════════════════════════════════════════════ */
function AexpensesStyles() {
  return (
    <style>{`
      @import url('https://fonts.googleapis.com/css2?family=Cinzel+Decorative:wght@400;700;900&family=Cinzel:wght@400;500;600;700&family=EB+Garamond:ital,wght@0,400;0,500;0,600;1,400;1,500&display=swap');

      :root {
        --gold-bright: #e8c46a; --gold-mid: #c9973a; --gold-dim: #8a6020;
        --crimson-deep: #1a0404; --crimson-mid: #2c0808; --crimson-rich: #3d0c0c;
        --hero-1: #2e0c0c; --hero-2: #1c0505; --hero-3: #100202;
        --parchment: #FDF3E3; --parchment-dark: #f5e8cc;
        --text-dark: #1a0404; --text-mid: #4a1a08;
      }

      /* ══ HERO ══ */
      .adv-hero { position: relative; overflow: hidden; background: linear-gradient(160deg, var(--hero-1) 0%, var(--hero-2) 50%, var(--hero-3) 100%); padding: 40px 32px 0; display: flex; flex-direction: column; align-items: center; border-radius: 16px 16px 0 0; }
      .adv-hero-bg { position: absolute; inset: 0; pointer-events: none; background: radial-gradient(ellipse at 15% 30%, rgba(160,35,35,0.35) 0%, transparent 50%), radial-gradient(ellipse at 85% 70%, rgba(80,15,15,0.3) 0%, transparent 45%); }
      .adv-hero-rings { position: absolute; top: 30px; right: 60px; width: 230px; height: 230px; pointer-events: none; }
      .adv-hero-ring { position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%); border-radius: 50%; border: 1px solid rgba(232,196,106,0.07); animation: heroRingPulse 4s ease-in-out infinite alternate; }
      .adv-hero-ring:nth-child(odd) { border-style: dashed; }
      @keyframes heroRingPulse { from { opacity: 0.3; } to { opacity: 0.8; } }
      .adv-hero-medallion { width: 90px; height: 90px; border-radius: 50%; background: radial-gradient(circle at 40% 35%, #5a1515 0%, #2a0707 55%, #0e0101 100%); border: 1px solid rgba(232,196,106,0.3); display: flex; align-items: center; justify-content: center; position: relative; margin-bottom: 20px; box-shadow: 0 0 30px rgba(232,196,106,0.15), inset 0 1px 0 rgba(255,248,231,0.06); }
      .adv-hero-med-ring { position: absolute; inset: -5px; border-radius: 50%; border: 1px dashed rgba(232,196,106,0.25); animation: slowSpin 10s linear infinite; }
      .adv-hero-med-ring-2 { inset: -10px; border-style: dotted; border-color: rgba(232,196,106,0.12); animation-duration: 18s; animation-direction: reverse; }
      @keyframes slowSpin { to { transform: rotate(360deg); } }
      .adv-hero-face { position: absolute; inset: 0; border-radius: 50%; display: flex; align-items: center; justify-content: center; opacity: 0; transform: scale(0.7) rotateY(90deg); transition: opacity 0.55s ease, transform 0.55s ease; }
      .adv-hero-face.visible { opacity: 1; transform: scale(1) rotateY(0deg); }
      .adv-hero-om { font-family: 'Cinzel Decorative', cursive; font-size: 34px; background: linear-gradient(180deg, #fff8e7, var(--gold-bright), var(--gold-mid)); -webkit-background-clip: text; -webkit-text-fill-color: transparent; filter: drop-shadow(0 0 8px rgba(232,196,106,0.5)); }
      .adv-hero-logo-img { width: 54px; height: 54px; object-fit: contain; filter: drop-shadow(0 0 8px rgba(232,196,106,0.4)); }
      .adv-hero-med-halo { position: absolute; inset: -16px; border-radius: 50%; background: radial-gradient(circle, rgba(232,196,106,0.08) 0%, transparent 65%); pointer-events: none; }
      .adv-hero-text { text-align: center; position: relative; z-index: 1; }
      .adv-hero-eyebrow { font-family: 'Cinzel', serif; font-size: 8.5px; letter-spacing: 2px; color: rgba(232,196,106,0.35); text-transform: uppercase; margin-bottom: 10px; }
      .adv-hero-title { font-family: 'Cinzel Decorative', cursive; font-size: 28px; font-weight: 900; background: linear-gradient(135deg, #fffaf0, var(--gold-bright), var(--gold-mid)); -webkit-background-clip: text; -webkit-text-fill-color: transparent; line-height: 1.2; margin-bottom: 8px; }
      .adv-hero-sub { font-family: 'EB Garamond', serif; font-style: italic; font-size: 15px; color: rgba(245,228,176,0.4); margin-bottom: 20px; }
      .adv-hero-rule { width: 60%; max-width: 400px; height: 1px; margin: 0 auto 24px; background: linear-gradient(90deg, transparent, rgba(201,151,58,0.4), transparent); }
      .adv-stat-row { display: flex; gap: 14px; flex-wrap: wrap; justify-content: center; padding: 0 0 28px; position: relative; z-index: 1; }
      .adv-stat-card { background: rgba(255,255,255,0.04); border: 1px solid rgba(201,151,58,0.18); padding: 14px 22px; text-align: center; clip-path: polygon(10px 0%, 100% 0%, calc(100% - 10px) 100%, 0% 100%); transition: background 0.2s, border-color 0.2s; min-width: 90px; }
      .adv-stat-card:hover { background: rgba(201,151,58,0.08); border-color: rgba(201,151,58,0.35); }
      .adv-stat-val { font-family: 'Cinzel Decorative', cursive; font-size: 22px; font-weight: 900; color: var(--scolor, var(--gold-bright)); }
      .adv-stat-label { font-family: 'Cinzel', serif; font-size: 8px; letter-spacing: 1.5px; text-transform: uppercase; color: rgba(245,228,176,0.4); margin-top: 3px; }

      /* ══ TOOLBAR ══ */
      .adv-toolbar { display: flex; flex-wrap: wrap; gap: 12px; align-items: center; padding: 20px 28px 12px; background: var(--parchment); border-bottom: 1px solid rgba(201,151,58,0.15); box-shadow: 0 4px 12px rgba(26,4,4,0.06); }
      .adv-search-wrap { flex: 1; min-width: 220px; display: flex; align-items: center; background: #fff9f0; border: 1px solid rgba(201,151,58,0.28); clip-path: polygon(10px 0%, calc(100% - 10px) 0%, 100% 50%, calc(100% - 10px) 100%, 10px 100%, 0% 50%); overflow: hidden; transition: border-color 0.2s, box-shadow 0.2s; }
      .adv-search-wrap:focus-within { border-color: var(--gold-mid); box-shadow: 0 0 0 3px rgba(201,151,58,0.1); }
      .adv-search-icon { margin: 0 10px 0 18px; color: var(--gold-dim); font-size: 13px; flex-shrink: 0; }
      .adv-search-input { flex: 1; border: none; outline: none; background: transparent; font-family: 'EB Garamond', serif; font-size: 15px; color: var(--text-dark); padding: 11px 8px; }
      .adv-search-input::placeholder { color: rgba(74,26,8,0.28); font-style: italic; }
      .adv-search-clear { background: none; border: none; cursor: pointer; padding: 0 14px; color: rgba(74,26,8,0.35); font-size: 11px; transition: color 0.2s; }
      .adv-search-clear:hover { color: #8b1a1a; }
      .adv-filter-trigger-btn { display: flex; align-items: center; gap: 8px; font-family: 'Cinzel', serif; font-size: 9.5px; font-weight: 700; letter-spacing: 1.5px; text-transform: uppercase; background: linear-gradient(135deg, var(--hero-1), var(--hero-2)); border: 1px solid rgba(201,151,58,0.4); color: var(--gold-bright); padding: 10px 18px; cursor: pointer; clip-path: polygon(10px 0%, calc(100% - 10px) 0%, 100% 50%, calc(100% - 10px) 100%, 10px 100%, 0% 50%); transition: all 0.2s; position: relative; }
      .adv-filter-trigger-btn:hover { border-color: var(--gold-mid); background: linear-gradient(135deg, #3d1010, #2a0808); }
      .adv-filter-badge { position: absolute; top: -6px; right: -6px; background: #8b1a1a; color: white; font-size: 9px; width: 16px; height: 16px; border-radius: 50%; display: flex; align-items: center; justify-content: center; font-family: sans-serif; font-weight: bold; border: 1px solid var(--parchment); }
      .adv-dd-chevron { font-size: 9px; color: rgba(232,196,106,0.5); transition: transform 0.25s; }
      .adv-dd-chevron.open { transform: rotate(180deg); }
      .adv-toolbar-btns { display: flex; gap: 8px; flex-wrap: wrap; margin-left: auto; }
      .adv-action-btn { display: flex; align-items: center; gap: 7px; font-family: 'Cinzel', serif; font-size: 9.5px; font-weight: 700; letter-spacing: 1.5px; text-transform: uppercase; border: none; padding: 10px 18px; cursor: pointer; clip-path: polygon(10px 0%, calc(100% - 10px) 0%, 100% 50%, calc(100% - 10px) 100%, 10px 100%, 0% 50%); transition: all 0.25s; }
      .adv-btn-export { background: linear-gradient(135deg, #1a4d1a, #2a6b2a); color: #c8f0c8; border: 1px solid rgba(42,107,42,0.4); }
      .adv-btn-export:hover { filter: brightness(1.1); }
      .adv-btn-refresh { background: rgba(201,151,58,0.1); border: 1px solid rgba(201,151,58,0.3); color: var(--gold-dim); }
      .adv-btn-refresh:hover { background: rgba(201,151,58,0.18); }
      .adv-btn-clear { background: rgba(139,26,26,0.08); border: 1px solid rgba(139,26,26,0.3); color: #8b1a1a; }
      .adv-btn-clear:hover { background: rgba(139,26,26,0.15); }
      .spin { animation: spinIcon 0.8s linear infinite; }
      @keyframes spinIcon { to { transform: rotate(360deg); } }

      /* ══ FILTER PANEL ══ */
      .adv-filter-panel { background: rgba(232,196,106,0.05); border-bottom: 1px solid rgba(201,151,58,0.15); padding: 16px 28px; animation: slideDown 0.3s ease; }
      @keyframes slideDown { from { opacity: 0; transform: translateY(-10px); } to { opacity: 1; transform: translateY(0); } }
      .adv-filter-row { display: flex; flex-wrap: wrap; gap: 20px; align-items: flex-end; }
      .adv-filter-item { display: flex; flex-direction: column; gap: 6px; min-width: 160px; flex: 1; }
      .adv-filter-label { font-family: 'Cinzel', serif; font-size: 8px; font-weight: 700; letter-spacing: 1.5px; text-transform: uppercase; color: var(--gold-dim); display: flex; align-items: center; gap: 5px; }
      .adv-filter-input, .adv-filter-select { width: 100%; padding: 8px 10px; background: #fff9f0; border: 1px solid rgba(201,151,58,0.28); font-family: 'EB Garamond', serif; font-size: 14px; color: var(--text-dark); outline: none; transition: border-color 0.2s; clip-path: polygon(6px 0%, calc(100% - 6px) 0%, 100% 50%, calc(100% - 6px) 100%, 6px 100%, 0% 50%); }
      .adv-filter-input:focus, .adv-filter-select:focus { border-color: var(--gold-mid); }
      .adv-filter-range { min-width: 300px; }
      .adv-range-inputs { display: flex; align-items: center; gap: 8px; width: 100%; }
      .adv-range-sep { color: var(--gold-dim); }
      .adv-filter-clear-btn { margin-top: 4px; background: none; border: none; font-family: 'Cinzel', serif; font-size: 8.5px; font-weight: 700; letter-spacing: 1px; text-transform: uppercase; color: #8b1a1a; cursor: pointer; padding: 2px 0; opacity: 0.7; transition: opacity 0.2s; }
      .adv-filter-clear-btn:hover { opacity: 1; }
      .adv-filter-note { font-family: 'EB Garamond', serif; font-style: italic; font-size: 10px; letter-spacing: 0; text-transform: none; color: rgba(139,26,26,0.6); margin-left: 5px; font-weight: 400; }
      .adv-filter-disabled { opacity: 0.4; pointer-events: none; }

      /* ══ RESULT INFO ══ */
      .adv-result-info { padding: 10px 28px 4px; font-family: 'Cinzel', serif; font-size: 9px; letter-spacing: 1px; color: rgba(74,26,8,0.45); background: var(--parchment); }
      .adv-result-info strong { color: var(--gold-dim); }

      /* ══ TABLE ══ */
      .adv-table-wrap { padding: 0 28px 40px; background: var(--parchment); overflow-x: auto; }
      .adv-table-scroll-container { overflow-x: auto; max-width: 100%; }
      .adv-table { width: 100%; border-collapse: separate; border-spacing: 0 6px; font-family: 'EB Garamond', serif; min-width: 700px; }
      .adv-table thead tr { background: linear-gradient(135deg, var(--hero-1) 0%, var(--hero-2) 55%, var(--hero-3) 100%); }
      .adv-table thead tr th { font-family: 'Cinzel', serif; font-size: 8.5px; font-weight: 700; letter-spacing: 2px; text-transform: uppercase; color: var(--gold-bright); padding: 14px 14px; text-align: left; border-bottom: 1px solid rgba(201,151,58,0.25); white-space: nowrap; position: relative; }
      .adv-table thead tr th:first-child { border-radius: 3px 0 0 3px; padding-left: 18px; }
      .adv-table thead tr th:last-child { border-radius: 0 3px 3px 0; }
      .adv-table thead tr th::after { content: ''; position: absolute; bottom: 0; left: 0; right: 0; height: 1px; background: linear-gradient(90deg, transparent, rgba(201,151,58,0.4), transparent); }
      .adv-table-row { background: linear-gradient(135deg, #FDF3E3, #faf0dc); animation: rowIn 0.35s ease both; transition: box-shadow 0.2s, transform 0.2s; }
      .adv-table-row:hover { box-shadow: 0 4px 18px rgba(26,4,4,0.1); transform: translateY(-1px); }
      @keyframes rowIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
      .adv-table-row td { padding: 12px 14px; vertical-align: middle; font-size: 14px; color: var(--text-mid); border-top: 1px solid rgba(201,151,58,0.1); border-bottom: 1px solid rgba(201,151,58,0.1); }
      .adv-table-row td:first-child { border-left: 1px solid rgba(201,151,58,0.1); border-radius: 2px 0 0 2px; }
      .adv-table-row td:last-child  { border-right: 1px solid rgba(201,151,58,0.1); border-radius: 0 2px 2px 0; }
      .adv-td-num  { font-family: 'Cinzel', serif; font-size: 10px; color: rgba(74,26,8,0.35); width: 36px; }
      .adv-td-date { font-family: 'Cinzel', serif; font-size: 10px; color: rgba(74,26,8,0.6); white-space: nowrap; }
      .adv-cat-name { font-family: 'Cinzel', serif; font-size: 11px; font-weight: 700; color: var(--text-dark); }
      .adv-devotee-name { font-family: 'Cinzel', serif; font-size: 11px; font-weight: 700; color: var(--text-dark); }
      .adv-td-mobile { font-family: 'Cinzel', serif; font-size: 11px; letter-spacing: 0.5px; }
      .adv-action-cell { display: flex; gap: 5px; align-items: center; }
      .adv-icon-btn { width: 30px; height: 30px; border: 1px solid; border-radius: 2px; display: flex; align-items: center; justify-content: center; font-size: 12px; cursor: pointer; background: none; transition: all 0.18s; flex-shrink: 0; }
      .adv-btn-view { border-color: rgba(201,151,58,0.3); color: var(--gold-dim); }
      .adv-btn-view:hover { background: rgba(201,151,58,0.12); color: var(--gold-mid); }
      .adv-btn-edit { border-color: rgba(42,107,42,0.3); color: rgba(42,107,42,0.75); }
      .adv-btn-edit:hover { background: rgba(42,107,42,0.1); color: #2a6b2a; }
      .adv-btn-delete-sm { border-color: rgba(139,26,26,0.25); color: rgba(139,26,26,0.7); }
      .adv-btn-delete-sm:hover { background: rgba(139,26,26,0.08); color: #8b1a1a; }

      /* ══ LOADER & EMPTY ══ */
      .adv-loader-wrap { display: flex; flex-direction: column; align-items: center; justify-content: center; padding: 80px 0; gap: 14px; }
      .adv-loader-om { font-family: 'Cinzel Decorative', cursive; font-size: 48px; color: var(--gold-bright); animation: omGlow 2s ease-in-out infinite alternate; }
      @keyframes omGlow { from { opacity: 0.6; } to { opacity: 1; filter: drop-shadow(0 0 20px rgba(232,196,106,0.6)); } }
      .adv-empty { display: flex; flex-direction: column; align-items: center; justify-content: center; padding: 70px 0; gap: 14px; text-align: center; }
      .adv-empty-om { font-size: 52px; opacity: 0.25; }
      .adv-empty-title { font-family: 'Cinzel Decorative', cursive; font-size: 20px; color: var(--crimson-rich); }
      .adv-empty-sub { font-family: 'EB Garamond', serif; font-style: italic; font-size: 15px; color: rgba(74,26,8,0.45); max-width: 320px; }

      /* ══ MODAL BASE ══ */
      .adv-modal-overlay { position: fixed; inset: 0; z-index: 1000; background: rgba(8,1,1,0.85); backdrop-filter: blur(7px); display: flex; align-items: center; justify-content: center; padding: 20px; animation: overlayIn 0.2s ease; }
      @keyframes overlayIn { from { opacity: 0; } to { opacity: 1; } }
      .adv-regal-modal { position: relative; width: 100%; background: linear-gradient(160deg, #FDF3E3, #faf0dc 55%, #f5e8cc); border: 1px solid rgba(201,151,58,0.35); box-shadow: 0 36px 110px rgba(26,4,4,0.7), 0 0 0 1px rgba(201,151,58,0.06) inset; animation: regalIn 0.35s cubic-bezier(0.23,1,0.32,1) both; overflow: hidden; }
      @keyframes regalIn { from { opacity: 0; transform: scale(0.93) translateY(26px); } to { opacity: 1; transform: scale(1) translateY(0); } }
      .adv-rm-topglow { position: absolute; top: 0; left: 0; right: 0; height: 3px; background: linear-gradient(90deg, transparent 0%, rgba(201,151,58,0.2) 20%, rgba(232,196,106,0.9) 50%, rgba(201,151,58,0.2) 80%, transparent 100%); z-index: 10; }
      .adv-cm-corner { position: absolute; width: 16px; height: 16px; border-color: rgba(201,151,58,0.5); border-style: solid; z-index: 10; }
      .adv-cm-corner.tl { top: 14px; left: 14px; border-width: 1.5px 0 0 1.5px; }
      .adv-cm-corner.tr { top: 14px; right: 14px; border-width: 1.5px 1.5px 0 0; }
      .adv-cm-corner.bl { bottom: 14px; left: 14px; border-width: 0 0 1.5px 1.5px; }
      .adv-cm-corner.br { bottom: 14px; right: 14px; border-width: 0 1.5px 1.5px 0; }

      /* Detail modal */
      .adv-detail-regal { max-width: 540px; max-height: 90vh; overflow-y: auto; display: flex; flex-direction: column; }
      .adv-detail-regal::-webkit-scrollbar { width: 7px; }
      .adv-detail-regal::-webkit-scrollbar-track { background: linear-gradient(180deg, rgba(201,151,58,0.04), rgba(201,151,58,0.08), rgba(201,151,58,0.04)); border-radius: 10px; border: 1px solid rgba(201,151,58,0.1); margin: 4px 0; }
      .adv-detail-regal::-webkit-scrollbar-thumb { background: linear-gradient(180deg, rgba(201,151,58,0.25), var(--gold-mid), var(--gold-bright), var(--gold-mid), rgba(201,151,58,0.25)); border-radius: 10px; border: 1px solid rgba(232,196,106,0.35); box-shadow: 0 0 6px rgba(232,196,106,0.3); }
      .adv-detail-regal::-webkit-scrollbar-thumb:hover { background: linear-gradient(180deg, var(--gold-mid), var(--gold-bright), #fff8e7, var(--gold-bright), var(--gold-mid)); }

      /* ══ EXPENSE FORM MODAL ══ */
      .adv-exp-form-regal { max-width: 660px; max-height: 92vh; display: flex; flex-direction: column; }

      /* Ornate scrollbar for form body */
      .adv-exp-scroll-body {
        flex: 1; overflow-y: auto; overflow-x: hidden;
        padding: 22px 28px 28px;
        scrollbar-width: thin;
        scrollbar-color: var(--gold-mid) rgba(201,151,58,0.08);
      }
      .adv-exp-scroll-body::-webkit-scrollbar { width: 7px; }
      .adv-exp-scroll-body::-webkit-scrollbar-track {
        background: linear-gradient(180deg, rgba(201,151,58,0.04) 0%, rgba(201,151,58,0.09) 50%, rgba(201,151,58,0.04) 100%);
        border-radius: 10px; border: 1px solid rgba(201,151,58,0.1); margin: 4px 0;
      }
      .adv-exp-scroll-body::-webkit-scrollbar-thumb {
        background: linear-gradient(180deg, rgba(201,151,58,0.2) 0%, var(--gold-mid) 30%, var(--gold-bright) 50%, var(--gold-mid) 70%, rgba(201,151,58,0.2) 100%);
        border-radius: 10px; border: 1px solid rgba(232,196,106,0.4);
        box-shadow: 0 0 8px rgba(232,196,106,0.3), inset 0 1px 0 rgba(255,255,255,0.2);
      }
      .adv-exp-scroll-body::-webkit-scrollbar-thumb:hover {
        background: linear-gradient(180deg, var(--gold-mid) 0%, var(--gold-bright) 40%, #fff8e7 50%, var(--gold-bright) 60%, var(--gold-mid) 100%);
        box-shadow: 0 0 12px rgba(232,196,106,0.55);
      }

      /* ── Category "select once" block ── */
      /* Global date + category row — side by side */
      .adv-exp-global-row { display: grid; grid-template-columns: 1fr 1.4fr; gap: 14px; margin-bottom: 20px; }
      .adv-exp-once-block { display: flex; flex-direction: column; gap: 7px; margin-bottom: 0; }

      /* Date input wrapper */
      .adv-exp-date-wrap { display: flex; align-items: center; background: #fff9f0; border: 1px solid rgba(201,151,58,0.3); border-radius: 3px; overflow: hidden; transition: border-color 0.25s, box-shadow 0.25s; }
      .adv-exp-date-wrap:focus-within { border-color: var(--gold-mid); box-shadow: 0 0 0 3px rgba(201,151,58,0.12); }
      .adv-exp-date-wrap.err { border-color: #b52020; }
      .adv-exp-date-input { flex: 1; border: none; outline: none; background: transparent; font-family: 'Cinzel', serif; font-size: 12px; color: var(--text-dark); padding: 12px 10px; cursor: pointer; }
      .adv-exp-once-label { display: flex; align-items: center; gap: 6px; font-family: 'Cinzel', serif; font-size: 8.5px; font-weight: 700; letter-spacing: 2px; text-transform: uppercase; color: var(--gold-dim); }
      .adv-exp-once-icon { font-size: 10px; color: var(--gold-mid); }
      .adv-exp-once-badge { font-family: 'EB Garamond', serif; font-style: italic; font-size: 10px; letter-spacing: 0; text-transform: none; font-weight: 400; color: rgba(74,26,8,0.38); margin-left: 4px; }
      .adv-exp-select-wrap { display: flex; align-items: center; background: #fff9f0; border: 1px solid rgba(201,151,58,0.3); border-radius: 3px; overflow: hidden; transition: border-color 0.25s, box-shadow 0.25s; position: relative; }
      .adv-exp-select-wrap:focus-within { border-color: var(--gold-mid); box-shadow: 0 0 0 3px rgba(201,151,58,0.12); }
      .adv-exp-select-wrap.err { border-color: #b52020; }
      .adv-exp-sel-icon { padding: 0 10px 0 14px; color: var(--gold-mid); font-size: 12px; flex-shrink: 0; }
      .adv-exp-sel-arrow { padding: 0 12px; color: var(--gold-dim); font-size: 9px; flex-shrink: 0; pointer-events: none; }
      .adv-exp-select { flex: 1; border: none; outline: none; background: transparent; font-family: 'EB Garamond', serif; font-size: 15px; color: var(--text-dark); padding: 12px 8px; -webkit-appearance: none; cursor: pointer; }

      /* ── Column headers for inline rows ── */
      .adv-exp-col-headers {
        display: grid;
        grid-template-columns: 28px 1fr 120px 32px;
        gap: 8px;
        padding: 0 6px 6px;
        border-bottom: 1px solid rgba(201,151,58,0.2);
        margin-bottom: 6px;
      }
      .adv-exp-col-headers > div {
        font-family: 'Cinzel', serif; font-size: 7.5px; font-weight: 700;
        letter-spacing: 1.5px; text-transform: uppercase;
        color: var(--gold-dim);
        display: flex; align-items: center; gap: 4px;
      }
      .adv-exp-ch-num { justify-content: center; }
      .adv-exp-ch-amount { justify-content: flex-end; }

      /* ── Inline row list ── */
      .adv-exp-rows-list { display: flex; flex-direction: column; gap: 6px; margin-bottom: 14px; }

      .adv-exp-inline-row {
        display: grid;
        grid-template-columns: 28px 1fr 120px 32px;
        gap: 8px;
        align-items: flex-start;
        padding: 8px 6px;
        background: rgba(253,243,227,0.7);
        border: 1px solid rgba(201,151,58,0.18);
        border-radius: 3px;
        transition: border-color 0.2s, background 0.2s;
      }
      .adv-exp-inline-row:hover { background: #fffaf0; border-color: rgba(201,151,58,0.35); }
      .adv-exp-inline-row.has-err { border-color: rgba(181,32,32,0.3); background: rgba(181,32,32,0.02); }

      .adv-exp-ir-num {
        font-family: 'Cinzel', serif; font-size: 10px; font-weight: 700;
        color: rgba(74,26,8,0.3); text-align: center; padding-top: 10px;
      }

      .adv-exp-ir-cell { display: flex; flex-direction: column; gap: 3px; min-width: 0; }

      .adv-exp-ir-input {
        width: 100%; border: 1px solid rgba(201,151,58,0.25); border-radius: 2px;
        background: #fff9f0; outline: none;
        font-family: 'EB Garamond', serif; font-size: 14px; color: var(--text-dark);
        padding: 9px 10px;
        transition: border-color 0.2s, box-shadow 0.2s;
      }
      .adv-exp-ir-input:focus { border-color: var(--gold-mid); box-shadow: 0 0 0 2px rgba(201,151,58,0.1); background: #fffcf5; }
      .adv-exp-ir-input.err { border-color: #b52020; }
      .adv-exp-ir-input[type="date"] { font-family: 'Cinzel', serif; font-size: 11px; }

      /* Amount cell */
      .adv-exp-ir-amt-wrap { display: flex; align-items: center; border: 1px solid rgba(201,151,58,0.25); border-radius: 2px; background: #fff9f0; overflow: hidden; transition: border-color 0.2s, box-shadow 0.2s; }
      .adv-exp-ir-amt-wrap:focus-within { border-color: var(--gold-mid); box-shadow: 0 0 0 2px rgba(201,151,58,0.1); }
      .adv-exp-ir-rupee { font-family: 'Cinzel', serif; font-size: 12px; font-weight: 700; color: var(--gold-mid); padding: 0 8px 0 10px; border-right: 1px solid rgba(201,151,58,0.18); flex-shrink: 0; line-height: 1; }
      .adv-exp-ir-amt-input { flex: 1; border: none; outline: none; background: transparent; font-family: 'Cinzel', serif; font-size: 13px; font-weight: 600; color: var(--text-dark); padding: 9px 8px; text-align: left; width: 100%; }
      .adv-exp-ir-amt-input::-webkit-inner-spin-button, .adv-exp-ir-amt-input::-webkit-outer-spin-button { -webkit-appearance: none; }
      .adv-exp-ir-amt-input[type=number] { -moz-appearance: textfield; }

      .adv-exp-ir-err { font-size: 9.5px; color: #b52020; font-family: 'EB Garamond', serif; font-style: italic; }

      /* Delete cell */
      .adv-exp-ir-del { display: flex; align-items: flex-start; padding-top: 8px; }
      .adv-exp-ir-del-btn { width: 24px; height: 24px; border: 1px solid rgba(139,26,26,0.3); background: rgba(139,26,26,0.04); color: rgba(139,26,26,0.7); border-radius: 2px; cursor: pointer; display: flex; align-items: center; justify-content: center; font-size: 10px; transition: all 0.18s; }
      .adv-exp-ir-del-btn:hover { background: rgba(139,26,26,0.12); color: #8b1a1a; border-color: rgba(139,26,26,0.5); }
      .adv-exp-ir-del-ph { width: 24px; height: 24px; }

      /* Add row + total row */
      .adv-exp-footer-row { display: flex; align-items: center; justify-content: center; gap: 16px; margin-bottom: 8px; flex-wrap: wrap; }
      .adv-exp-add-more-btn { display: flex; align-items: center; gap: 6px; border: 1px dashed rgba(201,151,58,0.4); background: rgba(201,151,58,0.05); color: var(--gold-dim); padding: 9px 16px; font-family: 'Cinzel', serif; font-size: 8.5px; font-weight: 700; text-transform: uppercase; letter-spacing: 1px; cursor: pointer; transition: all 0.2s; border-radius: 2px; }
      .adv-exp-add-more-btn:hover { border-color: var(--gold-mid); color: var(--gold-mid); background: rgba(201,151,58,0.1); }
      .adv-exp-total-chip { display: flex; align-items: center; gap: 8px; padding: 8px 16px; background: rgba(139,26,26,0.06); border: 1px solid rgba(139,26,26,0.2); border-radius: 2px; animation: chipIn 0.2s ease both; }
      @keyframes chipIn { from { opacity:0; transform: scale(0.9); } to { opacity:1; transform: scale(1); } }
      .adv-exp-total-label { font-family: 'Cinzel', serif; font-size: 8px; font-weight: 700; letter-spacing: 2px; text-transform: uppercase; color: rgba(139,26,26,0.6); }
      .adv-exp-total-val { font-family: 'Cinzel Decorative', cursive; font-size: 15px; font-weight: 900; color: #8b1a1a; }

      /* ── Edit form shared ── */
      .adv-edit-section-label { font-family: 'Cinzel', serif; font-size: 8px; font-weight: 700; letter-spacing: 2.5px; text-transform: uppercase; color: var(--gold-dim); margin-bottom: 18px; padding-bottom: 7px; border-bottom: 1px solid rgba(201,151,58,0.15); }
      .adv-edit-err { font-size: 10px; color: #b52020; font-family: 'EB Garamond', serif; font-style: italic; margin-top: 2px; }
      .adv-req { color: #b52020; font-weight: 900; margin-left: 2px; }
      .adv-edit-actions { display: flex; gap: 10px; align-items: center; justify-content: center; padding-top: 18px; border-top: 1px solid rgba(201,151,58,0.12); }
      .adv-edit-save-btn { display: inline-flex; align-items: center; gap: 8px; font-family: 'Cinzel', serif; font-size: 10px; font-weight: 700; letter-spacing: 2px; text-transform: uppercase; color: var(--crimson-deep); background: linear-gradient(135deg, #7a4e0c 0%, var(--gold-mid) 20%, var(--gold-bright) 40%, #fff8e7 55%, var(--gold-bright) 70%, var(--gold-mid) 85%, #6b4209 100%); border: none; padding: 13px 28px; cursor: pointer; clip-path: polygon(14px 0%, calc(100% - 14px) 0%, 100% 50%, calc(100% - 14px) 100%, 14px 100%, 0 50%); box-shadow: 0 6px 24px rgba(201,151,58,0.3), inset 0 1px 0 rgba(255,255,255,0.25); transition: all 0.35s ease; position: relative; overflow: hidden; }
      .adv-edit-save-btn::before { content: ''; position: absolute; top: -50%; left: -70%; width: 40%; height: 200%; background: rgba(255,255,255,0.25); transform: skewX(-18deg); transition: left 0.55s ease; pointer-events: none; }
      .adv-edit-save-btn:hover::before { left: 130%; }
      .adv-edit-save-btn:hover { transform: translateY(-2px); box-shadow: 0 12px 32px rgba(201,151,58,0.5); }
      .adv-edit-save-btn:disabled { opacity: 0.38; cursor: not-allowed; transform: none !important; box-shadow: none; }
      .adv-edit-saving-spinner { width: 12px; height: 12px; border-radius: 50%; border: 2px solid rgba(26,4,4,0.2); border-top-color: var(--crimson-deep); animation: spinIcon 0.7s linear infinite; flex-shrink: 0; }

      /* ══ HEADER ══ */
      .adv-rm-header { position: relative; overflow: hidden; background: linear-gradient(160deg, var(--hero-1), var(--hero-2) 55%, var(--hero-3)); padding: 38px 28px 28px; text-align: center; border-bottom: 1px solid rgba(201,151,58,0.2); }
      .adv-rm-header-bg { position: absolute; inset: 0; background: radial-gradient(ellipse at 50% 40%, rgba(150,30,30,0.28) 0%, transparent 65%); pointer-events: none; }
      .adv-rm-header-inner { position: relative; z-index: 1; display: flex; flex-direction: column; align-items: center; gap: 8px; }
      .adv-rm-close { position: absolute; top: 16px; right: 16px; z-index: 5; background: rgba(255,255,255,0.06); border: 1px solid rgba(232,196,106,0.2); color: rgba(232,196,106,0.55); border-radius: 50%; width: 30px; height: 30px; display: flex; align-items: center; justify-content: center; cursor: pointer; font-size: 11px; transition: all 0.2s; }
      .adv-rm-close:hover { background: rgba(255,255,255,0.12); color: var(--gold-bright); transform: rotate(90deg); }
      .adv-rm-header-icon-wrap { position: relative; width: 72px; height: 72px; border-radius: 50%; display: flex; align-items: center; justify-content: center; background: rgba(232,196,106,0.08); border: 1px solid rgba(232,196,106,0.2); }
      .adv-rm-hicon-ring { position: absolute; inset: -5px; border-radius: 50%; border: 1px dashed rgba(232,196,106,0.28); animation: slowSpin 9s linear infinite; }
      .adv-rm-hicon { font-size: 28px; color: var(--gold-bright); filter: drop-shadow(0 0 10px rgba(232,196,106,0.4)); position: relative; z-index: 1; }
      .adv-rm-header-eyebrow { font-family: 'Cinzel', serif; font-size: 7.5px; letter-spacing: 3px; color: rgba(232,196,106,0.3); text-transform: uppercase; }
      .adv-rm-title { font-family: 'Cinzel Decorative', cursive; font-size: 22px; font-weight: 900; background: linear-gradient(135deg, #fffaf0, var(--gold-bright), var(--gold-mid)); -webkit-background-clip: text; -webkit-text-fill-color: transparent; line-height: 1.25; }
      .adv-rm-name-tag { font-family: 'Cinzel', serif; font-size: 11px; letter-spacing: 1px; color: rgba(245,228,176,0.4); }
      .adv-rm-rule { display: flex; align-items: center; justify-content: center; height: 1px; margin: 0 40px; background: linear-gradient(90deg, transparent, rgba(201,151,58,0.4), transparent); position: relative; z-index: 1; }
      .adv-rm-rule-ornament { position: absolute; font-size: 12px; color: var(--gold-mid); background: var(--parchment); padding: 0 8px; line-height: 1; }

      /* ══ DETAIL MODAL BODY ══ */
      .adv-detail-scroll-body { padding: 22px 28px 28px; position: relative; z-index: 1; overflow-y: auto; }
      .adv-detail-section-label { font-family: 'Cinzel', serif; font-size: 8px; font-weight: 700; letter-spacing: 2.5px; text-transform: uppercase; color: var(--gold-dim); margin-bottom: 14px; padding-bottom: 7px; border-bottom: 1px solid rgba(201,151,58,0.15); }
      .adv-detail-info-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; margin-bottom: 14px; }
      .adv-di-field { display: flex; align-items: flex-start; gap: 10px; padding: 10px 12px; background: rgba(201,151,58,0.04); border: 1px solid rgba(201,151,58,0.1); border-radius: 1px; transition: background 0.2s; }
      .adv-di-field:hover { background: rgba(201,151,58,0.08); }
      .adv-di-icon { color: var(--gold-dim); font-size: 13px; margin-top: 3px; flex-shrink: 0; }
      .adv-di-label { font-family: 'Cinzel', serif; font-size: 7.5px; letter-spacing: 1.5px; text-transform: uppercase; color: rgba(74,26,8,0.38); margin-bottom: 3px; }
      .adv-di-val { font-family: 'EB Garamond', serif; font-size: 15px; color: var(--text-mid); }
      .adv-detail-action-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(118px, 1fr)); gap: 8px; }
      .adv-action-tile { display: flex; flex-direction: column; align-items: center; gap: 5px; padding: 14px 10px; border: 1px solid; cursor: pointer; background: none; transition: all 0.2s; clip-path: polygon(6px 0%,calc(100% - 6px) 0%,100% 6px,100% calc(100% - 6px),calc(100% - 6px) 100%,6px 100%,0 calc(100% - 6px),0 6px); }
      .adv-action-tile:hover { transform: translateY(-2px); }
      .adv-at-icon { font-size: 18px; }
      .adv-at-label { font-family: 'Cinzel', serif; font-size: 8.5px; font-weight: 700; letter-spacing: 1px; text-transform: uppercase; }
      .adv-at-desc { font-family: 'EB Garamond', serif; font-style: italic; font-size: 10.5px; opacity: 0.7; }
      .adv-action-tile.danger { border-color: rgba(139,26,26,0.22); color: rgba(139,26,26,0.8); }
      .adv-action-tile.danger:hover { border-color: rgba(139,26,26,0.45); background: rgba(139,26,26,0.04); color: #8b1a1a; }

      /* ══ CONFIRM MODAL ══ */
      .adv-confirm-modal { position: relative; width: 100%; max-width: 420px; background: linear-gradient(160deg, #FDF3E3, #faf0dc 55%, #f5e8cc); border: 1px solid rgba(201,151,58,0.35); clip-path: polygon(0 18px,18px 0,calc(100% - 18px) 0,100% 18px,100% calc(100% - 18px),calc(100% - 18px) 100%,18px 100%,0 calc(100% - 18px)); box-shadow: 0 32px 90px rgba(26,4,4,0.68); animation: regalIn 0.3s cubic-bezier(0.23,1,0.32,1) both; overflow: hidden; }
      .adv-cm-topstrip { height: 3px; width: 100%; }
      .adv-cm-header { padding: 30px 28px 0; display: flex; flex-direction: column; align-items: center; text-align: center; gap: 10px; }
      .adv-cm-icon-wrap { position: relative; width: 68px; height: 68px; border-radius: 50%; display: flex; align-items: center; justify-content: center; border: 1px solid; }
      .adv-cm-icon-ring { position: absolute; inset: -5px; border-radius: 50%; border: 1px dashed; animation: slowSpin 8s linear infinite; }
      .adv-cm-icon { font-size: 24px; position: relative; z-index: 1; }
      .adv-cm-title-area { display: flex; flex-direction: column; align-items: center; gap: 4px; }
      .adv-cm-eyebrow { font-family: 'Cinzel', serif; font-size: 7px; letter-spacing: 3px; color: rgba(74,26,8,0.3); text-transform: uppercase; }
      .adv-cm-title { font-family: 'Cinzel Decorative', cursive; font-size: 20px; font-weight: 900; background: linear-gradient(135deg, var(--hero-1), var(--hero-2)); -webkit-background-clip: text; -webkit-text-fill-color: transparent; }
      .adv-cm-divider { display: flex; align-items: center; justify-content: center; height: 1px; margin: 18px 36px 0; background: linear-gradient(90deg, transparent, rgba(201,151,58,0.35), transparent); position: relative; }
      .adv-cm-divider-gem { width: 6px; height: 6px; transform: rotate(45deg); position: absolute; }
      .adv-cm-body { padding: 16px 32px 20px; }
      .adv-cm-message { font-family: 'EB Garamond', serif; font-size: 16px; color: var(--text-mid); line-height: 1.7; text-align: center; }
      .adv-cm-actions { display: flex; gap: 10px; justify-content: center; flex-wrap: wrap; padding: 0 28px 24px; }
      .adv-cm-confirm-btn { background: var(--cbg) !important; color: #e8ffe8 !important; border-color: var(--cborder) !important; }
      .adv-cm-confirm-btn:hover { filter: brightness(1.12); }
      .adv-cm-bottomstrip { height: 1px; margin: 0 24px 14px; background: linear-gradient(90deg, transparent, rgba(201,151,58,0.25), transparent); }

      /* ══ BUTTONS ══ */
      .adv-btn { display: inline-flex; align-items: center; gap: 7px; font-family: 'Cinzel', serif; font-size: 9.5px; font-weight: 700; letter-spacing: 1.5px; text-transform: uppercase; border: none; padding: 10px 18px; cursor: pointer; clip-path: polygon(10px 0%,calc(100% - 10px) 0%,100% 50%,calc(100% - 10px) 100%,10px 100%,0 50%); transition: all 0.2s; border: 1px solid transparent; }
      .adv-btn:disabled { opacity: 0.38; cursor: not-allowed; }
      .adv-btn-ghost { background: none; border-color: rgba(149,27,27,0.5); color: rgb(74,26,8); font-weight: bold; }
      .adv-btn-ghost:hover { background: rgba(201,151,58,0.06); }

      /* ══ TOAST ══ */
      .adv-toast-container { position: fixed; top: 28px; right: 28px; z-index: 2000; animation: toastIn 0.4s cubic-bezier(0.23,1,0.32,1) both; }
      @keyframes toastIn { from { opacity: 0; transform: translateX(60px) scale(0.95); } to { opacity: 1; transform: translateX(0) scale(1); } }
      .adv-toast-card { position: relative; width: 340px; background: linear-gradient(145deg,#FDF3E3,#faf0dc,#f5e8cc); border: 1px solid var(--border-color); clip-path: polygon(0 12px,12px 0,calc(100% - 12px) 0,100% 12px,100% calc(100% - 12px),calc(100% - 12px) 100%,12px 100%,0 calc(100% - 12px)); box-shadow: 0 12px 32px rgba(26,4,4,0.14), inset 0 1px 0 rgba(255,255,255,0.7); padding: 0 0 14px; overflow: hidden; }
      .adv-toast-top-bar { height: 3px; background: linear-gradient(90deg,transparent,var(--accent) 20%,var(--accent) 80%,transparent); margin-bottom: 16px; }
      .adv-toast-corner { position: absolute; width: 10px; height: 10px; border-color: rgba(201,151,58,0.45); border-style: solid; }
      .adv-toast-corner.tl { top: 12px; left: 12px; border-width: 1px 0 0 1px; }
      .adv-toast-corner.tr { top: 12px; right: 12px; border-width: 1px 1px 0 0; }
      .adv-toast-corner.bl { bottom: 8px; left: 12px; border-width: 0 0 1px 1px; }
      .adv-toast-corner.br { bottom: 8px; right: 12px; border-width: 0 1px 1px 0; }
      .adv-toast-medallion { position: absolute; left: 18px; top: 26px; width: 36px; height: 36px; border-radius: 50%; border: 1px solid; display: flex; align-items: center; justify-content: center; }
      .adv-toast-spin-ring { position: absolute; inset: -3px; border-radius: 50%; border: 1px dashed rgba(201,151,58,0.3); animation: slowSpin 8s linear infinite; }
      .adv-toast-icon-inner { font-size: 14px; position: relative; z-index: 1; }
      .adv-toast-content { padding: 0 40px 0 66px; }
      .adv-toast-label-row { display: flex; align-items: center; gap: 6px; margin-bottom: 5px; }
      .adv-toast-symbol { font-family: 'Cinzel Decorative', cursive; font-size: 9px; opacity: 0.7; }
      .adv-toast-title { font-family: 'Cinzel', serif; font-size: 10px; font-weight: 700; letter-spacing: 2px; text-transform: uppercase; flex: 1; }
      .adv-toast-rule { height: 1px; margin-bottom: 7px; }
      .adv-toast-message { font-family: 'EB Garamond', serif; font-style: italic; font-size: 14px; color: rgba(74,26,8,0.75); line-height: 1.5; }
      .adv-toast-bottom-rule { height: 1px; margin: 12px 20px 0; background: linear-gradient(90deg,transparent,rgba(201,151,58,0.25),transparent); }
      .adv-toast-close { position: absolute; top: 22px; right: 14px; background: none; border: none; font-size: 13px; cursor: pointer; opacity: 0.45; transition: opacity 0.2s; }
      .adv-toast-close:hover { opacity: 0.9; }

      /* ══ MOBILE CARDS ══ */
      .adv-card-list { display: flex; flex-direction: column; gap: 12px; padding-top: 4px; }
      .adv-mob-card { background: linear-gradient(145deg, #FDF3E3, #faf0dc); border: 1px solid rgba(201,151,58,0.2); border-radius: 12px; box-shadow: 0 2px 12px rgba(26,4,4,0.08); animation: rowIn 0.35s ease both; overflow: hidden; position: relative; }
      .adv-mob-card::before { content: ''; position: absolute; top: 0; left: 0; right: 0; height: 2px; background: linear-gradient(90deg, transparent, rgba(201,151,58,0.5), transparent); }
      .adv-mob-card-header { display: flex; align-items: center; justify-content: space-between; padding: 14px 14px 10px; border-bottom: 1px solid rgba(201,151,58,0.1); }
      .adv-mob-receipt { font-family: 'Cinzel', serif; font-size: 12px; font-weight: 700; color: var(--gold-dim); }
      .adv-mob-meta { padding: 10px 14px; display: flex; flex-direction: column; gap: 5px; border-bottom: 1px solid rgba(201,151,58,0.08); }
      .adv-mob-meta-item { display: flex; align-items: flex-start; gap: 7px; font-family: 'EB Garamond', serif; font-size: 13px; color: rgba(74,26,8,0.6); }
      .adv-mob-meta-icon { color: var(--gold-dim); font-size: 11px; margin-top: 2px; flex-shrink: 0; }
      .adv-mob-actions { display: flex; gap: 8px; flex-wrap: wrap; padding: 10px 14px 12px; align-items: center; }
      .adv-mob-btn { display: inline-flex; align-items: center; gap: 5px; font-family: 'Cinzel', serif; font-size: 8.5px; font-weight: 700; letter-spacing: 1px; text-transform: uppercase; border: 1px solid; padding: 8px 14px; cursor: pointer; background: none; transition: all 0.18s; border-radius: 20px; }
      .adv-mob-btn.mob-view { border-color: rgba(201,151,58,0.35); color: var(--gold-dim); }
      .adv-mob-btn.mob-view:hover { background: rgba(201,151,58,0.1); }
      .adv-mob-btn.mob-edit { border-color: rgba(42,107,42,0.35); color: rgba(42,107,42,0.8); }
      .adv-mob-btn.mob-edit:hover { background: rgba(42,107,42,0.08); color: #2a6b2a; }
      .adv-mob-btn.mob-delete { border-color: rgba(139,26,26,0.22); color: rgba(139,26,26,0.7); margin-left: auto; }
      .adv-mob-btn.mob-delete:hover { background: rgba(139,26,26,0.08); color: #8b1a1a; }
      .adv-status-pill { font-family: 'Cinzel', serif; font-size: 8.5px; font-weight: 700; letter-spacing: 1.5px; text-transform: uppercase; padding: 4px 10px; border: 1px solid; border-radius: 20px; display: inline-flex; align-items: center; }

      /* ══ RESPONSIVE: hide/show ══ */
      .adv-desktop-only { display: table; }
      .adv-mobile-only  { display: none !important; }

      /* ══ TABLET (769–1024px) ══ */
      @media (max-width: 1024px) and (min-width: 769px) {
        .adv-exp-col-headers { grid-template-columns: 28px 1fr 110px 32px; }
        .adv-exp-inline-row  { grid-template-columns: 28px 1fr 110px 32px; }
      }

      /* ══ MOBILE (≤768px) ══ */
      @media (max-width: 768px) {
        /* Show/hide */
        .adv-desktop-only { display: none !important; }
        .adv-mobile-only  { display: flex !important; }

        /* Hero */
        .adv-hero { padding: 22px 16px 0; border-radius: 0; }
        .adv-hero-medallion { width: 68px; height: 68px; margin-bottom: 12px; }
        .adv-hero-om { font-size: 24px; }
        .adv-hero-title { font-size: 20px; }
        .adv-hero-sub { font-size: 13px; margin-bottom: 12px; }
        .adv-hero-rings { display: none; }
        .adv-stat-row { display: grid; grid-template-columns: 1fr 1fr; gap: 8px; width: 100%; padding: 0 0 18px; }
        .adv-stat-card { min-width: unset; padding: 12px 10px; clip-path: none; border-radius: 8px; }
        .adv-stat-val { font-size: 16px; }

        /* Toolbar */
        .adv-toolbar { padding: 12px 14px 10px; gap: 9px; flex-direction: column; align-items: stretch; }
        .adv-search-wrap { min-width: unset; width: 100%; clip-path: none; border-radius: 28px; }
        .adv-filter-trigger-btn { width: 100%; clip-path: none; border-radius: 28px; justify-content: space-between; padding: 11px 16px; }
        .adv-toolbar-btns { width: 100%; display: grid; grid-template-columns: 1fr 1fr; gap: 8px; margin-left: 0; }
        .adv-action-btn { clip-path: none; border-radius: 28px; justify-content: center; padding: 11px 10px; font-size: 9px; }

        /* Filter panel */
        .adv-filter-panel { padding: 12px 14px; }
        .adv-filter-row { flex-direction: column; gap: 12px; }
        .adv-filter-range { min-width: unset; }
        .adv-range-inputs { flex-direction: row; gap: 6px; }
        .adv-filter-input, .adv-filter-select { clip-path: none; border-radius: 8px; }

        /* Result info + table area */
        .adv-result-info { padding: 8px 14px 4px; }
        .adv-table-wrap { padding: 0 14px 32px; }

        /* Modals — slide up from bottom */
        .adv-modal-overlay { padding: 0; align-items: flex-end; }
        .adv-regal-modal {
          clip-path: none !important;
          border-radius: 22px 22px 0 0 !important;
          max-height: 93vh;
          border-bottom: none;
          width: 100%;
          max-width: 100% !important;
        }
        .adv-regal-modal::after {
          content: '';
          position: absolute; top: 8px; left: 50%;
          transform: translateX(-50%);
          width: 44px; height: 4px; border-radius: 2px;
          background: rgba(232,196,106,0.28);
          pointer-events: none; z-index: 20; display: block;
        }

        /* Expense form modal on mobile */
        .adv-exp-form-regal { max-width: 100%; border-radius: 22px 22px 0 0 !important; }

        .adv-exp-scroll-body { padding: 16px 14px 24px; }

        /* Global date+category row stacks on mobile */
        .adv-exp-global-row { grid-template-columns: 1fr; gap: 12px; }
        .adv-exp-select-wrap { border-radius: 28px; }
        .adv-exp-date-wrap { border-radius: 28px; }
        .adv-exp-date-input { font-size: 13px; }
        .adv-exp-select { font-size: 14px; }

        /* Inline rows → stacked card layout on mobile */
        .adv-exp-col-headers { display: none; }
        .adv-exp-inline-row {
          display: flex;
          flex-direction: column;
          gap: 8px;
          padding: 12px;
          border-radius: 10px;
        }
        .adv-exp-ir-num { font-size: 9px; padding-top: 0; text-align: left; }
        .adv-exp-ir-cell { width: 100%; }
        .adv-exp-ir-input { border-radius: 8px; font-size: 15px; padding: 11px 12px; }
        .adv-exp-ir-input[type="date"] { font-size: 13px; }
        .adv-exp-ir-amt-wrap { border-radius: 8px; }
        .adv-exp-ir-del { align-items: flex-end; flex-direction: row; justify-content: flex-end; }
        .adv-exp-ir-del-btn { width: 32px; height: 32px; border-radius: 8px; }

        /* Footer row */
        .adv-exp-footer-row { flex-direction: column; align-items: center; gap: 8px; }
        .adv-exp-add-more-btn { border-radius: 28px; justify-content: center; padding: 12px; font-size: 10px; }
        .adv-exp-total-chip { border-radius: 8px; justify-content: center; }

        /* Actions */
        .adv-edit-actions { flex-direction: column; gap: 8px; }
        .adv-edit-save-btn { clip-path: none; border-radius: 28px; width: 100%; justify-content: center; }
        .adv-btn { clip-path: none; border-radius: 28px; padding: 12px 18px; }
        .adv-btn-ghost { width: 100%; justify-content: center; }

        /* Header */
        .adv-rm-header { padding: 26px 16px 20px; }
        .adv-rm-header-icon-wrap { width: 56px; height: 56px; }
        .adv-rm-hicon { font-size: 22px; }
        .adv-rm-title { font-size: 18px; }

        /* Detail modal */
        .adv-detail-scroll-body { padding: 14px 14px 28px; }
        .adv-detail-info-grid { grid-template-columns: 1fr; gap: 8px; }
        .adv-detail-action-grid { grid-template-columns: 1fr; }
        .adv-action-tile { clip-path: none; border-radius: 10px; flex-direction: row; gap: 10px; padding: 12px; }

        /* Confirm modal */
        .adv-confirm-modal {
          clip-path: none !important;
          border-radius: 22px 22px 0 0 !important;
          max-width: 100%; width: 100%; border-bottom: none;
        }

        /* Toast */
        .adv-toast-container { top: auto; bottom: 14px; right: 14px; left: 14px; }
        .adv-toast-card { width: 100%; clip-path: none; border-radius: 12px; }
      }

      /* ══ SMALL PHONES (≤420px) ══ */
      @media (max-width: 420px) {
        .adv-hero-title { font-size: 17px; }
        .adv-hero-eyebrow { display: none; }
        .adv-stat-row { grid-template-columns: 1fr 1fr; }
        .adv-rm-title { font-size: 16px; }
      }
    `}</style>
  );
}